import React, { useEffect } from "react";
import UserScreenWindow from "../Components/UserScreenWindow";
import {
  getAllDocs,
  getAllDocsSnap,
  setDocData,
} from "../../../utils/firebase/Firebase.utils";
import NotificationCard from "./NotificationCard";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import IOSSpinner from "../../../components/IOSSpinner";
import { IoNotificationsOutline } from "react-icons/io5";
import { MdMarkEmailRead } from "react-icons/md";
import { BiBell, BiBellOff } from "react-icons/bi";

export default function NotificationsUser() {
  const [notifications, setNotifications] = React.useState([]);
  const [filteredNotifications, setFilteredNotifications] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [filterType, setFilterType] = React.useState("unread"); // "all", "read", "unread"
  const currentUser = useSelector((state) => state.userObject);

  useEffect(() => {
    const fetchNotifications = async () => {
      await getAllDocsSnap("notifications", (data) => {
        const userNotifications = data.filter(
          (notification) => notification.to === currentUser.email
        );
        const sortedNotifications = userNotifications.sort(
          (a, b) => b.dateTime.seconds - a.dateTime.seconds
        );
        setNotifications(sortedNotifications);
        setFilteredNotifications(sortedNotifications);
        setIsLoading(false);
      });
    };
    fetchNotifications();
  }, []);

  useEffect(() => {
    // Apply filter when filterType or notifications change
    if (filterType === "all") {
      setFilteredNotifications(notifications);
    } else if (filterType === "read") {
      setFilteredNotifications(
        notifications.filter((notification) => notification.read === true)
      );
    } else if (filterType === "unread") {
      setFilteredNotifications(
        notifications.filter((notification) => notification.read === false)
      );
    }
  }, [filterType, notifications]);

  const markAllAsRead = async () => {
    setIsLoading(true);
    try {
      // Get all unread notifications
      const unreadNotifications = notifications.filter(
        (notification) => notification.read === false
      );

      // Update each notification to mark as read
      const updatePromises = unreadNotifications.map((notification) =>
        setDocData("notifications", notification.id, { read: true })
      );

      await Promise.all(updatePromises);

      // Update local state
      const updatedNotifications = notifications.map((notification) => ({
        ...notification,
        read: true,
      }));

      setNotifications(updatedNotifications);
      setFilteredNotifications(updatedNotifications);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const sampleObject = {
    read: true,
    dateTime: {
      seconds: 1742726502,
      nanoseconds: 344000000,
    },
    type: "gatePass",
    to: "atmagat@gmail.com",
    user: "atmagat@gmail.com",
    subject: "Gate Pass Status Update",
    id: "2AxHbkwEdHBmzAKlZkWU",
    message:
      "Your gate pass (Unit 6e - Tower 2) status has been updated to approved",
  };

  if (isLoading) {
    return (
      <UserScreenWindow label="Notifications">
        <div className="flex items-center justify-center min-h-[60vh]">
          <IOSSpinner />
        </div>
      </UserScreenWindow>
    );
  }

  return (
    <UserScreenWindow label="Notifications">
      <div className="px-4 w-full mx-auto">
        {/* Filter tabs and Mark all as read button */}
        <div className="flex justify-around items-center mb-6 flex-wrap">
          <div
            className="inline-flex rounded-lg shadow-sm w-full flex-grow"
            role="group "
          >
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium border border-[#363062] rounded-l-lg flex items-center justify-center gap-2 transition-all ${
                filterType === "all"
                  ? "bg-[#363062] text-white"
                  : "bg-white text-[#363062] hover:bg-[#363062]/10"
              }`}
              onClick={() => setFilterType("all")}
            >
              <BiBell className="text-base" />
              All
            </button>
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium border-t border-b border-[#363062] flex items-center justify-center gap-2 transition-all ${
                filterType === "unread"
                  ? "bg-[#363062] text-white"
                  : "bg-white text-[#363062] hover:bg-[#363062]/10"
              }`}
              onClick={() => setFilterType("unread")}
            >
              <IoNotificationsOutline className="text-base" />
              Unread
            </button>
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium border border-[#363062] rounded-r-lg flex items-center justify-center gap-2 transition-all ${
                filterType === "read"
                  ? "bg-[#363062] text-white"
                  : "bg-white text-[#363062] hover:bg-[#363062]/10"
              }`}
              onClick={() => setFilterType("read")}
            >
              <BiBellOff className="text-base" />
              Read
            </button>
          </div>

          {notifications.some(
            (notification) => notification.read === false
          ) && (
            <button
              type="button"
              className="px-4 py-2 mt-4 text-xs font-medium text-white bg-[#8FCD00] rounded-lg hover:bg-[#8FCD00]/90 focus:outline-none focus:ring-2 focus:ring-[#8FCD00]/50 focus:ring-offset-2 flex items-center gap-2 transition-all shadow-sm"
              onClick={markAllAsRead}
            >
              <MdMarkEmailRead className="text-base" />
              Mark All as Read
            </button>
          )}
        </div>

        {filteredNotifications.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100 w-full">
            <div className="text-[#363062]/40 mb-4 w-full">
              <IoNotificationsOutline className="mx-auto h-12 w-12" />
            </div>
            <h3 className="text-base font-medium text-[#363062] mb-2">
              No {filterType !== "all" ? filterType : ""} notifications yet
            </h3>
            <p className="text-[#363062]/60 text-sm">
              {filterType === "all"
                ? "You'll see your notifications here when they arrive."
                : `No ${filterType} notifications to display.`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification, index) => (
              <Link
                key={index}
                to={`/notif-details/${notification.id}`}
                className="block transition-all hover:scale-[1.01] active:scale-[0.99]"
              >
                <NotificationCard notification={notification} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </UserScreenWindow>
  );
}